import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { BookOpen, HeartPulse, Briefcase, HandHeart, Star } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { t } from "@/lib/translations";

const B = "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-07/prod_agent_8873ce71-5b0c-4935-a990-b42d3a2b9b9c";
const DOMAIN_IMGS = {
  education:      `${B}/IMG_0649_57924384b05d47e795e0a27c8bb791c5.jpeg`,
  health:         `${B}/IMG_0643_99ffdbe59d9342c0b8cb40665577f776.jpeg`,
  entrepreneurship:`${B}/IMG_0654_4eee87e6ee52484d8722dfb85ddb311c.jpeg`,
  humanitarian:   `${B}/f3d63c7a-dfd7-48ea-9215-9cf5b0ba1eb4_683c15186887469a803e5ee84323aea9.jpeg`,
  talents:        `${B}/IMG_0669_7a7eb36185b94d0788ed53788189f6bb.jpeg`,
};

export default function Domains() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { lang } = useLang();
  const tx = t[lang].domains;

  const domains = [
    { icon: BookOpen, key:"education",       title:tx.education,       desc:tx.educationDesc,       color:"from-blue-950/85 to-blue-900/65",     accent:"text-blue-300",   border:"border-blue-700/30", img:DOMAIN_IMGS.education,      span:"lg:col-span-2" },
    { icon: HeartPulse,key:"health",         title:tx.health,          desc:tx.healthDesc,          color:"from-rose-950/85 to-rose-900/65",     accent:"text-rose-300",   border:"border-rose-700/30",  img:DOMAIN_IMGS.health,         span:"lg:col-span-1" },
    { icon: Briefcase, key:"entrepreneurship",title:tx.entrepreneurship,desc:tx.entrepreneurshipDesc,color:"from-amber-950/85 to-amber-900/65",   accent:"text-amber-300",  border:"border-amber-700/30", img:DOMAIN_IMGS.entrepreneurship,span:"lg:col-span-1" },
    { icon: HandHeart, key:"humanitarian",   title:tx.humanitarian,    desc:tx.humanitarianDesc,    color:"from-emerald-950/85 to-emerald-900/65",accent:"text-emerald-300",border:"border-emerald-700/30",img:DOMAIN_IMGS.humanitarian,   span:"lg:col-span-1" },
    { icon: Star,      key:"talents",        title:tx.talents,         desc:tx.talentsDesc,         color:"from-violet-950/85 to-violet-900/65",  accent:"text-violet-300", border:"border-violet-700/30", img:DOMAIN_IMGS.talents,        span:"lg:col-span-2" },
  ];

  return (
    <section id="actions" className="py-24 lg:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" ref={ref}>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }} className="text-center mb-16">
          <span className="inline-block text-accent text-sm font-semibold uppercase tracking-[0.15em] mb-3">{tx.tag}</span>
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{tx.title}</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">{tx.subtitle}</p>
        </motion.div>
        <div className="grid lg:grid-cols-3 gap-5">
          {domains.map((d, i) => {
            const Icon = d.icon;
            return (
              <motion.div key={d.key} initial={{ opacity: 0, y: 32 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.1 + i * 0.1 }}
                className={`group relative rounded-3xl overflow-hidden cursor-pointer aspect-[4/3] ${d.span}`}>
                <img src={d.img} alt={d.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className={`absolute inset-0 bg-gradient-to-t ${d.color} transition-opacity duration-300 group-hover:opacity-95`} />
                <div className={`absolute inset-0 rounded-3xl border ${d.border} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center"><Icon className={`w-5 h-5 ${d.accent}`} /></div>
                    <h3 className="text-xl font-bold text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{d.title}</h3>
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed max-w-sm group-hover:text-white/90 transition-colors duration-200">{d.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
